import React, { useState } from 'react';
import { Audit } from '@/firebase/auditService';
import { auditService } from '@/firebase/auditService';
import { useNotification } from '@/contexts/NotificationContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useAuth } from '@/contexts/AuthContext';
import { X, Save, AlertCircle } from 'lucide-react';

interface CreateTemplateFromAuditModalProps {
  audit: Audit;
  onClose: () => void;
  onSuccess: () => void;
}

const CreateTemplateFromAuditModal: React.FC<CreateTemplateFromAuditModalProps> = ({
  audit,
  onClose,
  onSuccess
}) => {
  const { showNotification } = useNotification();
  const { currentOrganization } = useOrganization();
  const { user } = useAuth();
  
  const [templateName, setTemplateName] = useState(`${audit.auditTemplateName} - Template`);
  const [description, setDescription] = useState(`Template created from audit completed on ${audit.completedAt?.toDate ? audit.completedAt.toDate().toLocaleDateString() : 'N/A'}`);
  const [isSaving, setIsSaving] = useState(false);

  const handleCreate = async () => {
    if (!templateName.trim()) {
      showNotification('Template name is required', 'error');
      return;
    }

    if (!currentOrganization?.id) {
      showNotification('Organization not found', 'error');
      return;
    }

    setIsSaving(true);
    try {
      // Create new audit template based on the completed audit
      const newTemplate = {
        name: templateName.trim(),
        description: description.trim() || undefined,
        auditType: audit.auditTemplateName.includes('SOC2') ? 'SOC2' :
                   audit.auditTemplateName.includes('ISO') ? 'ISO27001' :
                   audit.auditTemplateName.includes('HIPAA') ? 'HIPAA' :
                   audit.auditTemplateName.includes('PCI') ? 'PCI-DSS' :
                   audit.auditTemplateName.includes('GDPR') ? 'GDPR' : 'Custom',
        frequency: audit.frequency,
        complianceTemplateIds: audit.complianceTemplateIds || [],
        organizationId: currentOrganization.id,
        metadata: audit.auditTemplateName.includes('Custom') ? {
          customAuditTypeName: audit.auditTemplateName
        } : undefined
      };

      const userInfo = user ? { id: user.id, name: user.name || '', email: user.email || '' } : undefined;
      const result = await auditService.createTemplate(newTemplate, userInfo);

      if (result.success) {
        showNotification('Audit template created successfully from previous audit', 'success');
        onSuccess();
        onClose();
      } else {
        showNotification(`Error: ${result.error}`, 'error');
      }
    } catch (error: any) {
      console.error('Error creating template from audit:', error);
      showNotification(`Error: ${error.message}`, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-800">Create Template from Audit</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Creating template from:</p>
                <p>{audit.auditTemplateName} - {audit.locationName}</p>
                <p className="text-xs mt-1">
                  Completed: {audit.completedAt?.toDate ? audit.completedAt.toDate().toLocaleDateString() : 'N/A'} • 
                  Score: {audit.completionPercentage || 0}% • 
                  {audit.complianceTemplateIds?.length || 0} compliance items
                </p>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Template Name *
            </label>
            <input
              type="text"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              placeholder="Enter template name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              rows={3}
              placeholder="Enter template description"
            />
          </div>

          <div className="bg-gray-50 rounded-lg p-3">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Template Details</h3>
            <div className="space-y-1 text-sm text-gray-600">
              <div><span className="font-medium">Type:</span> {audit.auditTemplateName.includes('SOC2') ? 'SOC2' :
                   audit.auditTemplateName.includes('ISO') ? 'ISO 27001' :
                   audit.auditTemplateName.includes('HIPAA') ? 'HIPAA' :
                   audit.auditTemplateName.includes('PCI') ? 'PCI-DSS' :
                   audit.auditTemplateName.includes('GDPR') ? 'GDPR' : 'Custom'}</div>
              <div><span className="font-medium">Frequency:</span> {audit.frequency === 'yearly' ? 'Yearly' :
                   audit.frequency === 'semi-yearly' ? 'Semi-Yearly' : 'Quarterly'}</div>
              <div><span className="font-medium">Compliance Items:</span> {audit.complianceTemplateIds?.length || 0}</div>
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md"
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={isSaving || !templateName.trim()}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            {isSaving ? 'Creating...' : 'Create Template'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateTemplateFromAuditModal;

